import { EchoServerError, assert } from './errors';
import { parseNavigation, parseView, submitSearchParams } from './response';

import type { Echo } from '../types';

const MEDIA_TYPE = 'text/vnd.betterment.echo.html';

export type RenderOptions = { url: string; signal: AbortSignal };
export type SubmitOptions = { action: string; method: string; body: FormData };

function isEchoResponse(response: Response): boolean {
  const contentType = response.headers.get('Content-Type') ?? '';
  return contentType.startsWith(MEDIA_TYPE);
}

export async function render({ url, signal }: RenderOptions): Promise<Echo> {
  const response = await fetch(url, {
    signal,
    headers: { accept: MEDIA_TYPE },
  });

  const navigation = parseNavigation(response);
  if (navigation) return { ...navigation, replace: true };

  assert(
    response.ok,
    new EchoServerError(`Echo rendering failed with HTTP status ${response.status}`, response.status)
  );

  assert(
    isEchoResponse(response),
    new EchoServerError('Echo rendering failed because the response was invalid', response.status)
  );

  return parseView(response);
}

export async function submit({ action, method, body }: SubmitOptions): Promise<Echo> {
  const url = new URL(action, window.location.href);

  if (method.toUpperCase() === 'GET') {
    return submitSearchParams(url, body);
  }

  assert(body.has('authenticity_token'), 'Echo submission requires an authenticity token field');

  const response = await fetch(url, {
    method,
    body,
    headers: { accept: MEDIA_TYPE },
  });

  const navigation = parseNavigation(response);
  if (navigation) return { ...navigation, replace: false };

  assert(
    isEchoResponse(response),
    new EchoServerError('Echo submission failed because the response was invalid', response.status)
  );

  return parseView(response);
}
